import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PopupModal from "./PopupModal";
import { useAuth } from "../../context/AuthContext";
import { fetchIdeas, fetchHackathonEvent } from "../../api/idea";
import { TrophyIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

async function fetchSelectionsForIdeas(token: string, ideaIds: string[]) {
  if (ideaIds.length === 0) return {};
  // Fetch all idea-selection records (no decision filter) and build a map ideaId -> decision
  const res = await fetch(`${BASE}/idea-selections?limit=200`, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return {};
  const data = await res.json();
  const map: Record<string, "selected" | "rejected"> = {};
  (data.items ?? []).forEach((s: any) => {
    const id = String(s.ideaId?._id ?? s.ideaId);
    if (ideaIds.includes(id)) map[id] = s.decision;
  });
  return map;
}

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  selected:            { bg: "bg-teal-100 border border-teal-200",   text: "text-teal-700",   label: "Selected" },
  under_mentor_review: { bg: "bg-orange-100 border border-orange-200", text: "text-orange-700", label: "Under Review" },
  submitted_final:     { bg: "bg-blue-100 border border-blue-200",   text: "text-blue-700",   label: "Submitted" },
  submitted_initial:   { bg: "bg-blue-100 border border-blue-200",   text: "text-blue-700",   label: "Submitted" },
  rejected:            { bg: "bg-red-100 border border-red-200",     text: "text-red-700",    label: "Rejected" },
};

const getStatus = (status: string) => statusConfig[status] ?? statusConfig["submitted_initial"];

const fmt = (date?: string) =>
  date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const statCards = [
  { key: "selected",    label: "Selected",     accent: "border-t-teal-600" },
  { key: "underReview", label: "Under Review",  accent: "border-t-orange-600" },
  { key: "submitted",   label: "Submitted",     accent: "border-t-blue-600" },
  { key: "rejected",    label: "Rejected",      accent: "border-t-red-700" },
];

export default function IdeaListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<any>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; filename: string; mimeType?: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchIdeas(token)
      .then(async (data) => {
        const items: any[] = data.items ?? [];
        // Overlay jury decisions from IdeaSelection onto each idea's status
        const ideaIds = items.map((i: any) => i._id);
        const decisionsMap = await fetchSelectionsForIdeas(token, ideaIds);
        const merged = items.map((idea: any) =>
          decisionsMap[idea._id] ? { ...idea, status: decisionsMap[idea._id] } : idea
        );
        setIdeas(merged);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const counts: Record<string, number> = {
    selected:    ideas.filter(i => i.status === "selected").length,
    underReview: ideas.filter(i => i.status === "under_mentor_review").length,
    submitted:   ideas.filter(i => i.status === "submitted_initial" || i.status === "submitted_final").length,
    rejected:    ideas.filter(i => i.status === "rejected").length,
  };

  return (
    <div className="min-h-screen bg-[#fafbff] p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#121a38]">Innovation Hub</h1>
        <p className="text-[#4a5565] mt-1">View and manage all your hackathon submissions</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
        {statCards.map(({ key, label, accent }) => (
          <div key={key} className={`bg-white rounded-xl shadow-sm border-t-4 ${accent} p-5`}>
            <p className="text-3xl font-bold text-[#121a38]">{counts[key]}</p>
            <p className="text-sm text-[#4a5565] mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      
<div className="bg-white rounded-2xl shadow-sm border border-gray-100">
  <div className="w-full overflow-x-auto">
    <table className="min-w-[900px] w-full text-sm">

          <thead>
            <tr className="bg-gradient-to-r from-[#0058ab] to-[#1db8f2] text-white text-xs uppercase tracking-wider">
              <th className="text-left px-6 py-4 font-semibold">Idea Title</th>
              <th className="text-left px-6 py-4 font-semibold">Hackathon</th>
              <th className="text-left px-6 py-4 font-semibold">Idea Statement</th>
              <th className="text-left px-6 py-4 font-semibold">Submission Date</th>
              <th className="text-left px-6 py-4 font-semibold">Status</th>
              <th className="text-left px-6 py-4 font-semibold">Mentor Feedback</th>
              <th className="text-left px-6 py-4 font-semibold">Final Submit</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400">Loading ideas...</td>
              </tr>
            ) : ideas.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400">No ideas found.</td>
              </tr>
            ) : (
              ideas.map((idea, i) => {
                const s = getStatus(idea.status);
                return (
                  <tr key={idea._id ?? i} className={`border-t border-gray-100 hover:bg-gray-50 transition-colors ${
                      idea.status === "selected" ? "bg-teal-50/40" : ""
                    }`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {idea.status === "selected" && (
                          <TrophyIcon className="h-4 w-4 text-[#ff9f43] flex-shrink-0" title="Selected by jury" />
                        )}
                        <button
                          onClick={() => setSelectedIdea(idea)}
                          className="font-semibold text-[#0058ab] hover:underline text-left"
                        >
                          {idea.title}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#4a5565]">{idea.hackathonId?.title ?? "—"}</td>
                    <td className="px-6 py-4 text-[#4a5565] max-w-xs truncate">{idea.problem}</td>
                    <td className="px-6 py-4 text-[#4a5565] whitespace-nowrap">{fmt(idea.submittedAt)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate("/user-dashboard/mentor-review", { state: { ideaId: idea._id, ideaTitle: idea.title } })}
                        className="text-xs px-3 py-1.5 rounded-lg text-blue-50 bg-blue-600 border border-blue-500 transition-colors font-medium"
                      >
                        View
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {(idea.status === "submitted_final" || idea.status === "selected") ? (
                        // Display uploaded files for submitted_final or selected ideas
                        <div className="space-y-2">
                          {idea.attachments && idea.attachments.length > 0 ? (
                            <div className="flex flex-col gap-2">
                              {idea.attachments.map((attachment: any, idx: number) => (
                                <div key={idx} className="flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                                  <svg className="h-4 w-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <button
                                    onClick={() => attachment.url && setPreviewAttachment({ url: attachment.url, filename: attachment.filename || `File ${idx + 1}`, mimeType: attachment.mimeType })}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[120px] text-left"
                                    title={attachment.filename || 'Preview file'}
                                  >
                                    {attachment.filename || `File ${idx + 1}`}
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No files uploaded</span>
                          )}
                        </div>
                      ) : idea.status === "under_mentor_review" ? (
                        <button
                          onClick={async () => {
                            const hackId = idea.hackathonId?._id ?? idea.hackathonId;
                            let finalSubmissionStart: string | undefined;
                            let finalSubmissionEnd: string | undefined;
                            try {
                              const h = await fetchHackathonEvent(token!, hackId);
                              finalSubmissionStart = h.phases?.submission?.finalSubmissionStart;
                              finalSubmissionEnd = h.phases?.submission?.finalSubmissionEnd;
                            } catch { /* navigate anyway */ }

                            // Check if submission is allowed
                            const now = new Date();
                            const submissionAllowed = finalSubmissionStart && finalSubmissionEnd
                              ? (() => {
                                  const start = new Date(finalSubmissionStart);
                                  const end = new Date(finalSubmissionEnd);
                                  end.setHours(23, 59, 59, 999);
                                  return now >= start && now <= end;
                                })()
                              : true;

                            if (!submissionAllowed) {
                              const notYetOpen = finalSubmissionStart ? now < new Date(finalSubmissionStart) : false;
                              toast.error(notYetOpen ? 'Final submission period has not started yet.' : 'Final submission deadline has passed.');
                              return;
                            }

                            navigate("/user-dashboard/final-submission", {
                              state: { ideaId: idea._id, hackathonId: hackId, finalSubmissionStart, finalSubmissionEnd },
                            });
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors text-white bg-teal-600 hover:bg-teal-700"
                        >
                          Upload
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Idea Modal */}
      {selectedIdea && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedIdea(null)}>
          <div className="w-[768px] h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <PopupModal
              idea={selectedIdea}
              onClose={() => setSelectedIdea(null)}
            />
          </div>
        </div>
      )}

      {/* File Preview Modal - Centered & Responsive */}
      {previewAttachment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => setPreviewAttachment(null)}>
          <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full h-[90vh] sm:h-[80vh] sm:max-w-2xl sm:max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-[#0058ab] to-[#1db8f2] flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold text-white truncate text-sm sm:text-base">{previewAttachment.filename}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                <button
                  onClick={() => window.open(previewAttachment.url, '_blank')}
                  className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded transition-colors whitespace-nowrap"
                >
                  Download
                </button>
                <button
                  onClick={() => setPreviewAttachment(null)}
                  className="p-1 sm:p-1.5 text-white hover:bg-white/20 rounded transition-colors flex-shrink-0"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Preview Content */}
            <div className="flex-1 bg-gray-50 overflow-auto">
              {previewAttachment.mimeType?.includes('pdf') || previewAttachment.filename.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewAttachment.url}
                  className="w-full h-full"
                  title="PDF Preview"
                />
              ) : previewAttachment.mimeType?.includes('image') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(previewAttachment.filename) ? (
                <div className="flex items-center justify-center h-full p-2 sm:p-4">
                  <img
                    src={previewAttachment.url}
                    alt={previewAttachment.filename}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 sm:gap-4 p-4 sm:p-8">
                  <svg className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-600 text-center text-sm sm:text-base">
                    Preview not available for this file type.<br />
                    <span className="text-xs sm:text-sm text-gray-500">Click download to view the file.</span>
                  </p>
                  <button
                    onClick={() => window.open(previewAttachment.url, '_blank')}
                    className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
}
